import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../../common/Icons';
import { AdminPersonnelPageHeader } from '../ui';
import { useApToast } from '../hooks/useApToast';
import ActPricingTable from '../pricing/ActPricingTable';
import PricingToolbar from '../pricing/PricingToolbar';
import actPricingService from '../../../services/actPricingService';
import { AP_COPY } from '../adminPersonnelCopy';
import {
  ACT_PRICING_TABS,
  EXAM_CATEGORY_TABS,
  REFERRAL_CATEGORY_TABS
} from '../pricing/actPricingUtils';
import {
  MEDICAL_EXAM_CATEGORIES,
  MEDICAL_COVERAGE_TYPES
} from '../../../constants/medicalExamCategories';
import { CARE_REFERRAL_CATEGORIES } from '../../../constants/careReferralCategories';
import '../AdminPersonnelPortal.css';
import '../pricing/AdminPersonnelPricing.css';

const P = AP_COPY.PRICING;

const DEFAULT_FILTERS = {
  search: '',
  pricingFilter: 'all',
  coverageFilter: ''
};

export default function AdminPersonnelPricingPage() {
  const { showToast } = useApToast();
  const [tab, setTab] = useState('consultations');
  const [rows, setRows] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState(DEFAULT_FILTERS.search);
  const [pricingFilter, setPricingFilter] = useState(DEFAULT_FILTERS.pricingFilter);
  const [coverageFilter, setCoverageFilter] = useState(DEFAULT_FILTERS.coverageFilter);
  const [examCategory, setExamCategory] = useState('ANALYSIS');
  const [referralCategory, setReferralCategory] = useState('');

  const hasActiveFilters =
    Boolean(search.trim()) ||
    pricingFilter !== DEFAULT_FILTERS.pricingFilter ||
    coverageFilter !== DEFAULT_FILTERS.coverageFilter;

  const clearFilters = useCallback(() => {
    setSearch(DEFAULT_FILTERS.search);
    setPricingFilter(DEFAULT_FILTERS.pricingFilter);
    setCoverageFilter(DEFAULT_FILTERS.coverageFilter);
  }, []);

  const handleTabChange = useCallback((nextTab) => {
    setTab(nextTab);
    clearFilters();
    setRows([]);
    setInitialLoading(true);
  }, [clearFilters]);

  const loadRows = useCallback(async () => {
    setRefreshing(true);
    try {
      const common = {
        pricing: pricingFilter !== 'all' ? pricingFilter : undefined,
        search: search.trim() || undefined
      };

      let res;
      if (tab === 'consultations') {
        res = await actPricingService.listConsultations({
          coverage_type: coverageFilter || undefined,
          ...common
        });
      } else if (tab === 'exams') {
        res = await actPricingService.listExams({
          category: examCategory,
          coverage_type: coverageFilter || undefined,
          ...common
        });
      } else if (tab === 'referrals') {
        res = await actPricingService.listReferrals({
          category: referralCategory || undefined,
          coverage_type: coverageFilter || undefined,
          ...common
        });
      } else if (tab === 'nursing-care') {
        res = await actPricingService.listNursingCare(common);
      } else if (tab === 'nursing-injections') {
        res = await actPricingService.listNursingInjections(common);
      } else if (tab === 'nursing-tests') {
        res = await actPricingService.listNursingTests(common);
      } else if (tab === 'vaccines') {
        res = await actPricingService.listVaccines(common);
      } else if (tab === 'family-planning') {
        res = await actPricingService.listFamilyPlanning(common);
      } else {
        res = await actPricingService.listNursingTests(common);
      }

      setRows(res?.success ? res.data || [] : []);
      if (!res?.success) showToast(res?.message || P.LOAD_ERROR, 'danger');
    } catch (e) {
      console.error(e);
      showToast(e.message || P.LOAD_ERROR, 'danger');
      setRows([]);
    } finally {
      setRefreshing(false);
      setInitialLoading(false);
    }
  }, [
    tab,
    pricingFilter,
    coverageFilter,
    examCategory,
    referralCategory,
    search,
    showToast
  ]);

  useEffect(() => {
    const t = setTimeout(() => {
      loadRows();
    }, 200);
    return () => clearTimeout(t);
  }, [loadRows]);

  const handleSave = useCallback(
    async (id, payload) => {
      try {
        let res;
        if (tab === 'consultations') res = await actPricingService.updateConsultation(id, payload);
        else if (tab === 'exams') res = await actPricingService.updateExam(id, payload);
        else if (tab === 'referrals') res = await actPricingService.updateReferral(id, payload);
        else if (tab === 'nursing-care') res = await actPricingService.updateNursingCare(id, payload);
        else if (tab === 'nursing-injections') res = await actPricingService.updateNursingInjection(id, payload);
        else if (tab === 'nursing-tests') res = await actPricingService.updateNursingTest(id, payload);
        else if (tab === 'vaccines') res = await actPricingService.updateVaccine(id, payload);
        else if (tab === 'family-planning') res = await actPricingService.updateFamilyPlanning(id, payload);
        else res = await actPricingService.updateNursingTest(id, payload);

        if (res?.success) {
          showToast(res.message || P.SAVE_OK, 'success');
          await loadRows();
        } else {
          showToast(res?.message || P.SAVE_ERROR, 'danger');
        }
      } catch (e) {
        showToast(e.message || P.SAVE_ERROR, 'danger');
      }
    },
    [tab, loadRows, showToast]
  );

  const getLabel = useMemo(() => {
    if (tab === 'consultations') {
      return (row) => row.service_line_label || row.service_line;
    }
    return (row) => row.name || '—';
  }, [tab]);

  const getSubLabel = useMemo(() => {
    if (tab === 'consultations') {
      return (row) =>
        MEDICAL_COVERAGE_TYPES[row.coverage_type]?.label || row.coverage_type || null;
    }
    if (tab === 'exams') {
      return (row) => {
        const parts = [];
        const cat = MEDICAL_EXAM_CATEGORIES[row.category]?.label;
        if (cat) parts.push(cat);
        const cov = MEDICAL_COVERAGE_TYPES[row.coverage_type]?.label;
        if (cov) parts.push(cov);
        if (row.analysis_family) parts.push(row.analysis_family.replace(/_/g, ' '));
        return parts.length ? parts.join(' · ') : null;
      };
    }
    if (tab === 'referrals') {
      return (row) => {
        const parts = [];
        const cat = CARE_REFERRAL_CATEGORIES[row.category]?.label;
        if (cat) parts.push(cat);
        const cov = MEDICAL_COVERAGE_TYPES[row.coverage_type]?.label;
        if (cov) parts.push(cov);
        if (row.partner_name) parts.push(row.partner_name);
        return parts.length ? parts.join(' · ') : null;
      };
    }
    if (tab === 'nursing-care') {
      return () => null;
    }
    if (tab === 'nursing-tests') {
      return () => null;
    }
    return () => null;
  }, [tab]);

  const showCoverageFilter = tab === 'consultations' || tab === 'exams' || tab === 'referrals';

  return (
    <>
      <div className="ap-dash ap-pricing-page">
        <AdminPersonnelPageHeader
          badge={P.BADGE}
          title={P.PAGE_TITLE}
          description={P.PAGE_SUBTITLE}
        />

        <section className="ap-pricing-shell">
          <div className="ap-pricing-tabs" role="tablist" aria-label="Familles d'actes">
            {ACT_PRICING_TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                className={`ap-pricing-tab ${tab === item.id ? 'is-active' : ''}`}
                onClick={() => handleTabChange(item.id)}
              >
                {item.icon ? (
                  <span className="ap-pricing-tab__icon" aria-hidden>
                    <Icon name={item.icon} size={15} />
                  </span>
                ) : null}
                {item.label}
              </button>
            ))}
          </div>

          {tab === 'exams' && (
            <div className="ap-pricing-segments" role="group" aria-label="Catégorie examen">
              {EXAM_CATEGORY_TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`ap-pricing-segment-btn ${examCategory === item.id ? 'is-active' : ''}`}
                  onClick={() => setExamCategory(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          {tab === 'referrals' && (
            <div className="ap-pricing-segments" role="group" aria-label="Catégorie prise en charge">
              {REFERRAL_CATEGORY_TABS.map((item) => (
                <button
                  key={item.id || 'all'}
                  type="button"
                  className={`ap-pricing-segment-btn ${referralCategory === item.id ? 'is-active' : ''}`}
                  onClick={() => setReferralCategory(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          <PricingToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder={P.SEARCH_PLACEHOLDER}
            pricingFilter={pricingFilter}
            onPricingFilterChange={setPricingFilter}
            coverageFilter={coverageFilter}
            onCoverageFilterChange={setCoverageFilter}
            showCoverageFilter={showCoverageFilter}
            resultCount={rows.length}
            refreshing={refreshing}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            clearFiltersLabel={P.CLEAR_FILTERS}
          />

          <ActPricingTable
            rows={rows}
            initialLoading={initialLoading}
            refreshing={refreshing}
            emptyMessage={P.EMPTY}
            emptyFilteredMessage={P.EMPTY_FILTERED}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            clearFiltersLabel={P.CLEAR_FILTERS}
            getLabel={getLabel}
            getSubLabel={getSubLabel}
            onSave={handleSave}
          />
        </section>
      </div>
    </>
  );
}
